(() => {
  if (customElements.get('mz-editorial-collection')) return;

  class MzEditorialCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.layout = this.dataset.layout || 'grid';
      this.mobileSlider = this.dataset.mobileSlider === 'true';
      this.track = this.querySelector('[data-editorial-track]');
      this.slides = Array.from(this.querySelectorAll('[data-editorial-slide]'));
      this.previousButton = this.querySelector('[data-editorial-previous]');
      this.nextButton = this.querySelector('[data-editorial-next]');
      this.dotsContainer = this.querySelector('[data-editorial-dots]');
      this.motionButton = this.querySelector('[data-editorial-motion]');
      this.status = this.querySelector('[data-editorial-status]');

      this.autoplay = !this.mobileSlider && this.dataset.autoplay === 'true';
      this.loop = !this.mobileSlider && this.dataset.loop === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.interval = Math.max(3000, Number.parseInt(this.dataset.interval, 10) || 6000);
      this.desktopItems = Math.max(1, Number.parseInt(this.dataset.desktopItems, 10) || 3);
      this.mobileItems = Math.max(1, Number.parseInt(this.dataset.mobileItems, 10) || 1);
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.currentPage = 0;
      this.userPaused = false;
      this.motionOverride = false;
      this.isHovering = false;
      this.isInViewport = true;
      this.timer = null;
      this.scrollFrame = null;
      this.scrollSettleTimer = null;
      this.isProgrammaticScroll = false;
      this.programmaticTarget = 0;
      this.abortController = new AbortController();
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.reducedMotionQuery.matches;

      if ((this.layout !== 'slider' && !this.mobileSlider) || !this.track || !this.slides.length) return;

      this.bindEvents();
      this.observeVisibility();
      this.rebuild();
    }

    disconnectedCallback() {
      this.clearTimer();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.scrollSettleTimer) window.clearTimeout(this.scrollSettleTimer);
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
      this.mobileQuery?.removeEventListener('change', this.handleBreakpointChange);
      this.reducedMotionQuery?.removeEventListener('change', this.handleReducedMotionChange);
      this.initialized = false;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.previousButton?.addEventListener('click', () => this.step(-1, true), { signal });
      this.nextButton?.addEventListener('click', () => this.step(1, true), { signal });
      this.motionButton?.addEventListener('click', () => this.toggleMotion(), { signal });

      this.dotsContainer?.addEventListener(
        'click',
        (event) => {
          const dot = event.target.closest('[data-editorial-page]');
          if (!dot) return;
          this.goToPage(Number.parseInt(dot.dataset.editorialPage, 10), true);
        },
        { signal }
      );

      this.track.addEventListener(
        'scroll',
        () => {
          if (this.isProgrammaticScroll) {
            if (Math.abs(this.track.scrollLeft - this.programmaticTarget) <= 2) this.finishProgrammaticScroll();
            return;
          }
          if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = window.requestAnimationFrame(() => this.syncFromScroll());
        },
        { signal, passive: true }
      );
      this.track.addEventListener(
        'scrollend',
        () => {
          if (this.isProgrammaticScroll) this.finishProgrammaticScroll();
          else this.syncFromScroll();
        },
        { signal }
      );

      if (this.pauseOnHover) {
        this.addEventListener(
          'pointerenter',
          () => {
            this.isHovering = true;
            this.refreshAutoplay();
          },
          { signal }
        );
        this.addEventListener(
          'pointerleave',
          () => {
            this.isHovering = false;
            this.refreshAutoplay();
          },
          { signal }
        );
      }

      document.addEventListener('visibilitychange', () => this.refreshAutoplay(), { signal });

      this.handleBreakpointChange = () => {
        this.currentPage = 0;
        this.rebuild();
      };
      this.handleReducedMotionChange = (event) => {
        this.reducedMotion = event.matches;
        if (!event.matches) this.motionOverride = false;
        this.updateMotionButton();
        this.refreshAutoplay();
      };

      this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
      this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isInViewport = entry.isIntersecting && entry.intersectionRatio >= 0.15;
          this.refreshAutoplay();
        },
        { threshold: [0, 0.15, 0.5] }
      );
      this.intersectionObserver.observe(this);
    }

    get itemsPerView() {
      if (this.mobileSlider) return this.mobileQuery.matches ? 1 : this.slides.length;
      return this.mobileQuery.matches ? this.mobileItems : this.desktopItems;
    }

    get pageStarts() {
      const perView = Math.min(this.itemsPerView, this.slides.length);
      const pageCount = Math.max(1, Math.ceil(this.slides.length / perView));
      const finalStart = Math.max(0, this.slides.length - perView);
      const starts = [];

      for (let page = 0; page < pageCount; page += 1) {
        starts.push(Math.min(page * perView, finalStart));
      }

      return [...new Set(starts)];
    }

    rebuild() {
      this.currentPage = Math.min(this.currentPage, this.pageStarts.length - 1);
      this.renderDots();
      this.goToPage(this.currentPage, false, false);
      this.updateControls();
      this.updateMotionButton();
      this.refreshAutoplay();
    }

    renderDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.replaceChildren();

      this.pageStarts.forEach((_start, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mz-editorial-collection__dot';
        button.dataset.editorialPage = String(index);
        button.setAttribute('aria-label', `Produktseite ${index + 1} von ${this.pageStarts.length}`);
        this.dotsContainer.append(button);
      });
    }

    step(direction, announce) {
      const total = this.pageStarts.length;
      if (total <= 1) return;

      let nextPage = this.currentPage + direction;
      if (this.loop) {
        nextPage = (nextPage + total) % total;
      } else {
        nextPage = Math.max(0, Math.min(total - 1, nextPage));
      }

      this.goToPage(nextPage, announce);
    }

    goToPage(page, announce = false, smooth = true) {
      const starts = this.pageStarts;
      if (!starts.length) return;

      const safePage = Math.max(0, Math.min(starts.length - 1, page));
      const target = this.slides[starts[safePage]];
      if (!target) return;

      this.currentPage = safePage;
      const left = Math.max(0, target.offsetLeft - this.track.offsetLeft);
      const useSmoothScroll = smooth && !this.reducedMotion;
      this.isProgrammaticScroll = true;
      this.programmaticTarget = left;
      if (this.scrollSettleTimer) window.clearTimeout(this.scrollSettleTimer);
      this.track.scrollTo({
        left,
        behavior: useSmoothScroll ? 'smooth' : 'auto'
      });
      this.scrollSettleTimer = window.setTimeout(
        () => this.finishProgrammaticScroll(),
        useSmoothScroll ? 1800 : 0
      );

      this.updateControls();
      if (announce) this.announce();
      this.refreshAutoplay();
    }

    finishProgrammaticScroll() {
      if (this.scrollSettleTimer) window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = null;
      this.isProgrammaticScroll = false;
      this.syncFromScroll();
    }

    syncFromScroll() {
      const starts = this.pageStarts;
      if (!starts.length) return;

      let closestPage = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      starts.forEach((slideIndex, pageIndex) => {
        const slide = this.slides[slideIndex];
        if (!slide) return;
        const distance = Math.abs(slide.offsetLeft - this.track.offsetLeft - this.track.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = pageIndex;
        }
      });

      if (closestPage !== this.currentPage) {
        this.currentPage = closestPage;
        this.updateControls();
      }
    }

    updateControls() {
      const total = this.pageStarts.length;
      const isStatic = total <= 1;
      this.toggleAttribute('data-slider-static', isStatic);

      if (this.previousButton) {
        this.previousButton.disabled = isStatic || (!this.loop && this.currentPage === 0);
      }
      if (this.nextButton) {
        this.nextButton.disabled = isStatic || (!this.loop && this.currentPage === total - 1);
      }

      this.dotsContainer?.querySelectorAll('[data-editorial-page]').forEach((dot, index) => {
        const active = index === this.currentPage;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    toggleMotion() {
      const explicitlyPaused = this.userPaused || (this.reducedMotion && !this.motionOverride);
      if (explicitlyPaused) {
        this.userPaused = false;
        this.motionOverride = true;
      } else {
        this.userPaused = true;
        this.motionOverride = false;
      }
      this.updateMotionButton();
      this.refreshAutoplay();
    }

    updateMotionButton() {
      if (!this.motionButton) return;
      const paused = this.userPaused || (this.reducedMotion && !this.motionOverride);
      this.motionButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.motionButton.setAttribute(
        'aria-label',
        paused ? 'Automatische Produktrotation fortsetzen' : 'Automatische Produktrotation pausieren'
      );
    }

    shouldPause() {
      return (
        !this.autoplay ||
        this.pageStarts.length <= 1 ||
        this.userPaused ||
        (this.reducedMotion && !this.motionOverride) ||
        this.designMode ||
        !this.isInViewport ||
        document.hidden ||
        (this.pauseOnHover && this.isHovering)
      );
    }

    refreshAutoplay() {
      this.clearTimer();
      if (this.shouldPause()) return;
      this.timer = window.setTimeout(() => this.step(1, false), this.interval);
    }

    clearTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }

    announce() {
      if (!this.status) return;
      this.status.textContent = `Produktseite ${this.currentPage + 1} von ${this.pageStarts.length}`;
    }
  }

  customElements.define('mz-editorial-collection', MzEditorialCollection);
})();
